// useNow — the SANCTIONED clock seam (docs/implement/ui-as-projection.md /
// scripts/checks/ui-projection.sh): the one place a component tree may read
// the impure wall clock. `Date.now()` + `setInterval` are isolated to this one
// hook (the sanctioned impure-shell pattern — an effect with cleanup); every
// consumer stays a pure render of the `now` it returns, so a raw `Date.now()`
// call elsewhere in the tree is the smell, not this hook.
//
// `periodMs` is coarse-grained by the caller (a minute-granularity relative
// label needs no sub-30s refresh), so many mounted timestamps stay cheap.
// Extracted from ChatMessage (the transcript's per-message relative time) so
// other surfaces — e.g. HeroHost's "senast sammanfattad" recency label — share
// the same seam instead of reaching for `Date.now()` directly.

import { useEffect, useState } from "react";

export const useNow = (periodMs: number): number => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), periodMs);
        return () => clearInterval(id);
    }, [periodMs]);
    return now;
};

// How long the calling component has been mounted, ticked every `periodMs`
// through the same seam. The anchor is the clock's first reading, so a caller
// mounted for exactly the span it measures (the chat's live row, mounted once
// per turn) reads that span without a timer or a wall-clock read of its own.
export const useElapsedSinceMount = (periodMs: number): number => {
    const now = useNow(periodMs);
    const [mountedAt] = useState(now);
    return now - mountedAt;
};
